import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Accordion from '../../../../components/Accordion';
import Select from '../../../../components/Inputs/Select';
import Input from '../../../../components/Inputs/Input';
import StepProgress from '../../../../components/StepProgress';
import Alert from '../../../../components/Alert';
import Divider from '../../../../components/Divider';
import DependentsDetails from '../../../../components/Cards/DependentsDetails';
import Button from '../../../../components/Button';
import FormContainer from '../../../../components/FormContainer';
import Radio from '../../../../components/Inputs/Radio';

import steps from '../../../../utils/steps.json';
import { checkIsSupport, cloneObject, handleTopCritics } from '../../../../utils';
import validate from '../../../../utils/validateForm';
import validators from '../../../../utils/defaultValidators';

import schema from './schema';

import { useGlobal } from '../../../../contexts/Global/context';

import { PageContainer, ContainerButton } from '../../../../styles/styles';
import { reativarBeneficiario } from '../../../../services';
import DatepickerMui from '../../../../components/Inputs/DatepickerMui';

function Beneficiario({ location: { dataBeneficiario, criticas } }) {
    const history = useHistory();

    const { dataActiveContract, setShowSupportModal } = useGlobal();

    const [alerts, setAlerts] = useState([]);

    useEffect(() => {
        if (criticas != null) {
            setAlerts([...criticas]);
        };
    }, []);

    const [loadingPost, setLoadingPost] = useState(false);
    const [dataForm, setDataForm] = useState(
        criticas ? dataBeneficiario :
            cloneObject({
                ...schema,
                tipoReativacao: {
                    ...schema.tipoReativacao,
                    required: dataBeneficiario.tipoAssociado !== 'D',
                },
                novoPlano: {
                    ...schema.novoPlano,
                    options: dataBeneficiario.novosPlanos.map((plan) => ({
                        ...plan,
                        nome: `${plan.codigo} - ${plan.nome}`,
                    })),
                },
            })
    );


    const [dataMovimentacao, setDataMovimentacao] = useState({});

    function handleInputBlur(event) {
        const { id, name, value } = event.target;
        const key = id || name;

        const form = cloneObject(dataForm);

        // Valida as regras de negocio do campo e, caso haja erro, altera o indice error
        if (typeof validators[key] === 'function') validators[key](form);

        // Valida os campos obrigatorios sem valores
        if (form[key].required && !value) {
            form[key].error = form[key].requiredMessage || 'Preencha com um valor válido.';
        }

        setDataForm({ ...form });
    }

    function handleChangeInput(event) {
        const { id, value, unmaskedValue, lookup } = event;

        const field = { ...dataForm[id], value, lookup, unmaskedValue, error: '' };

        setDataForm({
            ...dataForm,
            [id]: field,
        });
    }

    async function handlePostData() {
        setLoadingPost(true);

        const { validatedForm, isValid } = validate(dataForm);

        setDataForm({
            ...dataForm,
            ...validatedForm,
        });

        if (!isValid) {
            setTimeout(() => {
                const firstElementWithError = document.querySelector('.error');

                if (firstElementWithError)
                    firstElementWithError.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                    });
            }, 100);

            setLoadingPost(false);

            return;
        }

        if (checkIsSupport()) {
            setShowSupportModal(true);
            return;
        }

        const payload = {
            ...dataMovimentacao,
            numeroContrato: dataActiveContract.numero,
            codigoEmpresa: dataBeneficiario.empresa?.codigo,
            codigoPlanoNovo: dataForm.novoPlano.value,
            codigoPlano: dataBeneficiario.plano?.codigo,
            dataReativacao: dataForm.dataReativacao.value,
            incidencia: dataForm.tipoReativacao.value || 'D',
            numeroAssociadoOuCarteira: dataBeneficiario.numAssociado,
            nomeAssociado: dataBeneficiario.nomeCompleto,
            tipoAssociado: dataBeneficiario.tipoAssociado,
            referenciaMovimentacao: dataActiveContract.referenciaMovimentacao?.dataReferenciaMovimentacao,
        };

        const response = await reativarBeneficiario(payload);

        const movimentacao = response.movimentacoes[0];
        const criticasTratadas = handleTopCritics(movimentacao?.criticas);

        if (criticasTratadas.length > 0) {
            setAlerts([...criticasTratadas]);

            setDataMovimentacao({
                sequencialMovimentacao: movimentacao.sequencialMovimentacao,
                codigoSituacaoMovimentacao: movimentacao.codigoSituacaoMovimentacao,
            });

            setTimeout(() => {
                window.document.getElementById('page-content').scrollTo(0, 0);
            }, 100);

            setLoadingPost(false);

            return;
        }

        history.push('/movimentacao_cadastral/validar_reativar_beneficiario');

        setLoadingPost(false);
    }

    return (
        <PageContainer>
            <StepProgress steps={steps.reativarBeneficiario} activeStep={2} />

            {alerts.map((alert, index) => (
                <Alert key={() => index} type={alert.tipo} title={alert.descricao} message={alert.complemento} />
            ))}

            <Accordion title="Reativar beneficiário" hasCollapse={false}>
                <FormContainer>
                    <Input
                        field={{
                            id: 'carteirinha',
                            label: 'N° da carteirinha',
                            value: dataBeneficiario.carteirinha,
                            disabled: true,
                        }}
                    />

                    <Input
                        field={{
                            id: 'nomeCompleto',
                            label: 'Nome completo',
                            value: dataBeneficiario.nomeCompleto,
                            disabled: true,
                        }}
                    />

                    <Input
                        field={{
                            id: 'contrato',
                            label: 'Contrato atual',
                            value: `${dataActiveContract.numero} - ${dataActiveContract.nome}`,
                            disabled: true,
                        }}
                    />

                    <Input
                        field={{
                            id: 'empresa',
                            label: 'Empresa',
                            value: dataBeneficiario.empresa?.nome,
                            disabled: true,
                        }}
                    />
                </FormContainer>

                <Divider />

                <FormContainer>
                    <Input
                        field={{
                            id: 'planoAtual',
                            label: 'Plano atual',
                            value: dataBeneficiario.plano?.nome,
                            disabled: true,
                        }}
                    />

                    <DatepickerMui
                        field={dataForm.dataReativacao}
                        onBlur={handleInputBlur}
                        onChange={handleChangeInput}
                    />

                    <Select field={dataForm.novoPlano} onBlur={handleInputBlur} onChange={handleChangeInput} />

                    {dataBeneficiario.tipoAssociado !== 'D' && (
                        <Radio field={dataForm.tipoReativacao} onBlur={handleInputBlur} onChange={handleChangeInput} />
                    )}
                </FormContainer>
            </Accordion>

            {dataBeneficiario.dependentes?.length > 0 && <DependentsDetails dataBeneficiario={dataBeneficiario} />}

            <ContainerButton style={{ marginTop: '30px' }}>
                <Button id="backStep" type="secondary" onClick={() => history.goBack()}>
                    Voltar
                </Button>

                <Button
                    id="continueStep"
                    type="primary"
                    loading={loadingPost}
                    disabled={loadingPost}
                    onClick={handlePostData}
                >
                    Reativar
                </Button>
            </ContainerButton>
        </PageContainer>
    );
}

export default Beneficiario;
